'use client';

import { useMemo } from 'react';

import clsx from 'clsx';

import { parseTextToSpans } from '../../functions/parseTextToSpans';
import type { ChatTextMessage } from '../../types/ChatMessage';
import { MessageBubbleShell } from './MessageBubbleShell';

interface Props {
  message: ChatTextMessage;
  isOwn: boolean;
}

/**
 * Text bubble — mint background for own, neutral for received.
 * `whiteSpace: pre-wrap` preserves line breaks; URLs are linkified
 * via `parseTextToSpans` so users can click through.
 */
export function TextMessage({ message, isOwn }: Props) {
  const text = message.content?.text || message.text || '';
  const spans = useMemo(() => parseTextToSpans(text), [text]);
  const linkClass = isOwn
    ? 'underline decoration-black/40 hover:decoration-black'
    : 'text-brand-text hover:underline';

  return (
    <MessageBubbleShell message={message} isOwn={isOwn}>
      <div
        className={clsx(
          'whitespace-pre-wrap break-words px-3.5 py-2 text-sm',
          isOwn
            ? 'bg-brand text-content-on-brand'
            : 'bg-surface-3 text-content-1',
        )}
      >
        {spans.map((span, i) =>
          span.href ? (
            <a
              key={i}
              href={span.href}
              target="_blank"
              rel="noreferrer noopener"
              className={linkClass}
            >
              {span.text}
            </a>
          ) : (
            <span key={i}>{span.text}</span>
          ),
        )}
      </div>
    </MessageBubbleShell>
  );
}
